import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { CheckCircle2, AlertTriangle, XCircle, Award, Users, Lightbulb, Eye, ClipboardList, Zap } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Area, AreaChart
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0B1F3B] text-white px-3 py-2 rounded-lg shadow-xl text-xs">
      <p className="font-medium">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="text-slate-300">{p.name}: <span className="text-white font-bold">{p.value}%</span></p>
      ))}
    </div>
  );
};

function getCoachingPriorityBorder(type) {
  if (type === 'positive') return 'border-l-4 border-l-[#14B8A6]';
  if (type === 'low_readiness') return 'border-l-4 border-l-red-400';
  if (type === 'inactive') return 'border-l-4 border-l-[#F59E0B]';
  return 'border-l-4 border-l-[#2563EB]';
}

function getCoachingPriorityIconBg(type) {
  if (type === 'positive') return 'bg-[#14B8A6]/10';
  if (type === 'low_readiness') return 'bg-red-50';
  if (type === 'inactive') return 'bg-[#F59E0B]/10';
  return 'bg-[#2563EB]/10';
}

function CoachingPriorityIcon({ type }) {
  if (type === 'positive') return <Award className="w-4 h-4 text-[#14B8A6]" />;
  if (type === 'low_readiness') return <AlertTriangle className="w-4 h-4 text-red-500" />;
  if (type === 'inactive') return <Users className="w-4 h-4 text-[#F59E0B]" />;
  return <Lightbulb className="w-4 h-4 text-[#2563EB]" />;
}

function getActionIconBg(type) {
  if (type === 'review') return 'bg-[#2563EB]/10';
  if (type === 'assign') return 'bg-[#F59E0B]/10';
  return 'bg-[#14B8A6]/10';
}

function ActionIcon({ type }) {
  if (type === 'review') return <Eye className="w-4 h-4 text-[#2563EB]" />;
  if (type === 'assign') return <ClipboardList className="w-4 h-4 text-[#F59E0B]" />;
  return <Award className="w-4 h-4 text-[#14B8A6]" />;
}

const CHART_MARGIN_VERTICAL = { left: 20 };
const CHART_AXIS_TICK = { fill: '#94A3B8', fontSize: 11 };
const CHART_CATEGORY_TICK = { fill: '#475569', fontSize: 11 };
const RADAR_DOT = { fill: '#2563EB', r: 3 };
const AREA_DOT = { fill: '#2563EB', r: 4 };

export function ChartsSection({ dist, totalReps, trend, skillGaps, scenarioPerf, coachingPriorities, actions, getScoreColor, getScoreBg }) {
  const radarData = skillGaps.map(g => ({ subject: g.competency, score: g.score, fullMark: 100 }));

  return (
    <>
      {/* Distribution + Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="premium-card" data-testid="readiness-distribution">
          <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0B1F3B]">Readiness Distribution</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {[
              { label: "Field Ready", sublabel: "80-100%", count: dist.field_ready, color: "#14B8A6", icon: CheckCircle2 },
              { label: "Needs Coaching", sublabel: "60-79%", count: dist.needs_coaching, color: "#F59E0B", icon: AlertTriangle },
              { label: "Not Ready", sublabel: "Below 60%", count: dist.not_ready, color: "#EF4444", icon: XCircle },
            ].map(({ label, sublabel, count, color, icon: Icon }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                  <Icon className="w-4 h-4" style={{ color }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-[#0B1F3B]">{label}</span>
                    <span className="text-sm font-bold text-[#0B1F3B]">{count || 0}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${totalReps ? ((count || 0) / totalReps) * 100 : 0}%`, backgroundColor: color }} />
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{sublabel}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="premium-card lg:col-span-2" data-testid="readiness-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-[#0B1F3B]">Readiness Trend</CardTitle>
            <CardDescription className="text-slate-400">Average readiness score over time</CardDescription>
          </CardHeader>
          <CardContent>
            {trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563EB" stopOpacity={0.15} />
                      <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="date" tick={CHART_AXIS_TICK} tickFormatter={(d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} />
                  <YAxis domain={[0, 100]} tick={CHART_AXIS_TICK} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="score" name="Readiness" stroke="#2563EB" strokeWidth={2.5} fill="url(#trendGrad)" dot={AREA_DOT} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center"><p className="text-slate-400 text-sm">No trend data yet</p></div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Skill Gaps + Scenario Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="premium-card" data-testid="skill-gaps">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-[#0B1F3B]">Team Competency Map</CardTitle>
            <CardDescription className="text-slate-400">Average score per competency</CardDescription>
          </CardHeader>
          <CardContent>
            {radarData.length > 0 && radarData.some(d => d.score > 0) ? (
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radarData} outerRadius="75%">
                  <PolarGrid stroke="#E2E8F0" />
                  <PolarAngleAxis dataKey="subject" tick={CHART_CATEGORY_TICK} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar name="Team Average" dataKey="score" stroke="#2563EB" fill="#2563EB" fillOpacity={0.15} strokeWidth={2} dot={RADAR_DOT} />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <div className="space-y-3 mt-2">
                {skillGaps.map((g) => (
                  <div key={g.competency}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600">{g.competency}</span>
                      <span className={`text-sm font-bold ${getScoreColor(g.score)}`}>{g.score}%</span>
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${getScoreBg(g.score)}`} style={{ width: `${g.score}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="premium-card" data-testid="scenario-performance">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-[#0B1F3B]">Scenario Performance</CardTitle>
            <CardDescription className="text-slate-400">Average score by practice scenario</CardDescription>
          </CardHeader>
          <CardContent>
            {scenarioPerf.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={scenarioPerf} layout="vertical" margin={CHART_MARGIN_VERTICAL}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={CHART_AXIS_TICK} />
                  <YAxis type="category" dataKey="scenario" width={140} tick={CHART_CATEGORY_TICK} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="avg_score" name="Avg Score" radius={[0, 6, 6, 0]} fill="#2563EB" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center"><p className="text-slate-400 text-sm">No scenario data yet</p></div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Coaching Priorities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {coachingPriorities.map((cp, idx) => (
          <Card key={`cp-${cp.type}-${cp.title?.substring(0, 30)}`} className={`premium-card ${getCoachingPriorityBorder(cp.type)}`} data-testid={`coaching-priority-${idx}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${getCoachingPriorityIconBg(cp.type)}`}>
                  <CoachingPriorityIcon type={cp.type} />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[#0B1F3B] mb-1">{cp.title}</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">{cp.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {coachingPriorities.length === 0 && (
          <Card className="premium-card col-span-3 text-center py-8">
            <CardContent><p className="text-slate-400 text-sm">No coaching insights yet</p></CardContent>
          </Card>
        )}
      </div>

      {/* Suggested Actions */}
      {actions.length > 0 && (
        <Card className="premium-card mb-8" data-testid="suggested-actions">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-[#0B1F3B] flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#F59E0B]" /> Suggested Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {actions.map((a) => (
                <div key={`action-${a.type}-${a.action?.substring(0, 30)}`} className="p-4 bg-[#0B1F3B]/[0.02] rounded-xl border border-slate-200/60 hover:border-[#2563EB]/30 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getActionIconBg(a.type)}`}>
                      <ActionIcon type={a.type} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#0B1F3B]">{a.action}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{a.reason}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
